if (!customElements.get('sticky-atc-bar-custom')) {
  customElements.define(
    'sticky-atc-bar-custom',
    class StickyAtcBarCustom extends HTMLElement {
      constructor() {
        super();
        this.onSubmitClick = this.onSubmitClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.variantChangeUnsubscriber = undefined;
        this.observer = undefined;
        this.submitStateObserver = undefined;
        this.resizeObserver = undefined;
        this.onWindowResize = this.updateBodyOffset.bind(this);
        this.isBarVisible = false;
        this.hideOffsetTimer = undefined;
        this.onHideTransitionEnd = this.handleHideTransitionEnd.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.submitButtonId = this.dataset.submitButtonId;
        this.optionNames = this.parseOptionNames();
        this.colorOptionIndex = this.resolveColorOptionIndex(this.optionNames);
        this.sizeOptionIndex = this.resolveSizeOptionIndex(this.optionNames, this.colorOptionIndex);

        this.optionsEl = this.querySelector('[data-sticky-atc-options]');
        this.colorEl = this.querySelector('[data-sticky-atc-color]');
        this.colorValueEl = this.querySelector('[data-sticky-atc-color-value]');
        this.sizeEl = this.querySelector('[data-sticky-atc-size]');
        this.sizeValueEl = this.querySelector('[data-sticky-atc-size-value]');
        this.optionsSepEl = this.querySelector('[data-sticky-atc-options-sep]');
        this.priceEl = this.querySelector('[data-sticky-atc-price]');
        this.compareEl = this.querySelector('[data-sticky-atc-compare]');
        this.compareValueEl = this.querySelector('[data-sticky-atc-compare-value]');
        this.saleEl = this.querySelector('[data-sticky-atc-sale]');
        this.badgeEl = this.querySelector('[data-sticky-atc-badge]');
        this.mediaEl = this.querySelector('[data-sticky-atc-media]');
        this.imageEl = this.querySelector('[data-sticky-atc-image]');
        this.submitButton = this.querySelector('[data-sticky-atc-submit]');
        this.submitLabel = this.querySelector('[data-sticky-atc-submit-label]');

        this.submitButton?.addEventListener('click', this.onSubmitClick);
        window.addEventListener('resize', this.onWindowResize);

        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(() => {
            this.updateBodyOffset();
          });
          this.resizeObserver.observe(this);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.syncFromOriginal();
        this.setupObserver();
        this.setupSubmitStateObserver();
      }

      disconnectedCallback() {
        this.submitButton?.removeEventListener('click', this.onSubmitClick);
        window.removeEventListener('resize', this.onWindowResize);
        this.removeEventListener('transitionend', this.onHideTransitionEnd);
        this.variantChangeUnsubscriber?.();
        this.observer?.disconnect();
        this.submitStateObserver?.disconnect();
        this.resizeObserver?.disconnect();
        clearTimeout(this.hideOffsetTimer);
        this.clearBodyOffset();
        document.body.classList.remove('is-sticky-atc-visible');
        this.isBarVisible = false;
      }

      parseOptionNames() {
        try {
          const parsed = JSON.parse(this.dataset.productOptions || '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      resolveColorOptionIndex(optionNames) {
        const colorNames = new Set(['カラー', '色', 'color', 'colour']);
        const index = optionNames.findIndex(
          (name) => colorNames.has(String(name).toLowerCase()) || colorNames.has(String(name))
        );
        return index >= 0 ? index : 0;
      }

      resolveSizeOptionIndex(optionNames, colorIndex) {
        const sizeNames = new Set(['サイズ', 'size']);
        const namedIndex = optionNames.findIndex(
          (name) => sizeNames.has(String(name).toLowerCase()) || sizeNames.has(String(name))
        );
        if (namedIndex >= 0) return namedIndex;

        const fallbackIndex = optionNames.findIndex((_, index) => index !== colorIndex);
        return fallbackIndex >= 0 ? fallbackIndex : -1;
      }

      getOriginalSubmitButton() {
        return document.getElementById(this.submitButtonId);
      }

      setupObserver() {
        const originalButton = this.getOriginalSubmitButton();
        if (!originalButton || typeof IntersectionObserver === 'undefined') return;

        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.setVisible(scrolledPast);
          },
          { threshold: 0 }
        );

        this.observer.observe(originalButton);
      }

      setupSubmitStateObserver() {
        const originalButton = this.getOriginalSubmitButton();
        if (!originalButton || typeof MutationObserver === 'undefined') return;

        this.submitStateObserver = new MutationObserver(() => {
          this.syncSubmitState();
        });

        this.submitStateObserver.observe(originalButton, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['disabled', 'aria-disabled'],
        });
      }

      setVisible(visible) {
        if (this.isBarVisible === visible) return;
        this.isBarVisible = visible;

        if (visible) {
          this.cancelPendingHideCleanup();
          this.classList.add('is-visible');
          this.setAttribute('aria-hidden', 'false');
          document.body.classList.add('is-sticky-atc-visible');

          if (this.submitButton) {
            this.submitButton.tabIndex = 0;
          }

          this.syncFromOriginal();
          this.scheduleBodyOffsetUpdate();
          return;
        }

        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');

        if (this.submitButton) {
          this.submitButton.tabIndex = -1;
        }

        this.scheduleHideBodyOffsetCleanup();
      }

      cancelPendingHideCleanup() {
        clearTimeout(this.hideOffsetTimer);
        this.hideOffsetTimer = undefined;
        this.removeEventListener('transitionend', this.onHideTransitionEnd);
      }

      scheduleHideBodyOffsetCleanup() {
        this.cancelPendingHideCleanup();

        const prefersReducedMotion = window.matchMedia?.(
          '(prefers-reduced-motion: reduce)'
        )?.matches;

        if (prefersReducedMotion) {
          this.finishHideBodyOffsetCleanup();
          return;
        }

        this.addEventListener('transitionend', this.onHideTransitionEnd);
        this.hideOffsetTimer = setTimeout(() => {
          this.finishHideBodyOffsetCleanup();
        }, 400);
      }

      handleHideTransitionEnd(event) {
        if (event.target !== this || event.propertyName !== 'transform') return;
        this.finishHideBodyOffsetCleanup();
      }

      finishHideBodyOffsetCleanup() {
        this.cancelPendingHideCleanup();
        if (this.isBarVisible) return;

        this.clearBodyOffset();
        document.body.classList.remove('is-sticky-atc-visible');
      }

      scheduleBodyOffsetUpdate() {
        requestAnimationFrame(() => {
          this.updateBodyOffset();
        });
      }

      updateBodyOffset() {
        if (!this.isBarVisible) {
          return;
        }

        const height = Math.ceil(this.offsetHeight);
        document.body.style.setProperty('--sticky-atc-bar-height', `${height}px`);
      }

      clearBodyOffset() {
        document.body.style.removeProperty('--sticky-atc-bar-height');
      }

      onSubmitClick(event) {
        event.preventDefault();
        if (this.submitButton?.disabled) return;

        const originalButton = this.getOriginalSubmitButton();
        if (!originalButton || originalButton.disabled || originalButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        originalButton.click();
      }

      onVariantChange(event) {
        if (event?.data?.sectionId !== this.sectionId) return;

        const variant = event.data.variant;
        this.updateOptions(variant);
        this.updateImage(variant);
        this.syncFromOriginal();
        if (this.isBarVisible) {
          this.scheduleBodyOffsetUpdate();
        }
      }

      updateOptions(variant) {
        const options = variant?.options;
        if (!Array.isArray(options) || options.length === 0) {
          if (this.optionsEl) this.optionsEl.hidden = true;
          if (this.colorEl) this.colorEl.hidden = true;
          if (this.sizeEl) this.sizeEl.hidden = true;
          if (this.optionsSepEl) this.optionsSepEl.hidden = true;
          if (this.colorValueEl) this.colorValueEl.textContent = '';
          if (this.sizeValueEl) this.sizeValueEl.textContent = '';
          return;
        }

        const colorValue = options[this.colorOptionIndex] ?? options[0] ?? '';
        const sizeValue = this.sizeOptionIndex >= 0 ? options[this.sizeOptionIndex] ?? '' : '';
        const hasColor = Boolean(colorValue);
        const hasSize = Boolean(sizeValue) && this.sizeOptionIndex !== this.colorOptionIndex;

        if (this.colorValueEl) this.colorValueEl.textContent = colorValue;
        if (this.colorEl) this.colorEl.hidden = !hasColor;

        if (this.sizeValueEl) this.sizeValueEl.textContent = sizeValue;
        if (this.sizeEl) this.sizeEl.hidden = !hasSize;

        if (this.optionsSepEl) this.optionsSepEl.hidden = !(hasColor && hasSize);
        if (this.optionsEl) this.optionsEl.hidden = !(hasColor || hasSize);
      }

      updateImage(variant) {
        if (!this.imageEl || !this.mediaEl) return;

        const preview =
          variant?.featured_media?.preview_image ||
          variant?.featured_media?.src ||
          variant?.image;

        const src = typeof preview === 'string' ? preview : preview?.src;
        if (!src) return;

        const sizedSrc = src.includes('?') ? `${src}&width=160` : `${src}?width=160`;
        const sizedSrc1x = src.includes('?') ? `${src}&width=80` : `${src}?width=80`;

        this.imageEl.src = sizedSrc;
        this.imageEl.srcset = `${sizedSrc1x} 1x, ${sizedSrc} 2x`;
        this.imageEl.hidden = false;
        this.mediaEl.hidden = false;

        if (preview?.alt) {
          this.imageEl.alt = preview.alt;
        }
      }

      syncFromOriginal() {
        this.syncPrice();
        this.syncSubmitState();
        if (this.isBarVisible) {
          this.scheduleBodyOffsetUpdate();
        }
      }

      syncPrice() {
        if (!this.priceEl || !this.saleEl) return;

        const priceRoot = document.getElementById(`price-${this.sectionId}`);
        if (!priceRoot) return;

        const priceWrapper = priceRoot.querySelector('.price');
        const isOnSale = priceWrapper?.classList.contains('price--on-sale');

        if (isOnSale) {
          const compareSource = priceRoot.querySelector('.price__sale s.price-item--regular');
          const saleSource = priceRoot.querySelector('.price__sale .price-item--sale');
          const badgeSource = priceRoot.querySelector('.price__badge-discount-rate');

          if (saleSource) {
            this.saleEl.textContent = saleSource.textContent.trim();
          }

          if (this.compareEl && this.compareValueEl && compareSource) {
            this.compareValueEl.textContent = compareSource.textContent.trim();
            this.compareEl.hidden = false;
          } else if (this.compareEl) {
            this.compareEl.hidden = true;
          }

          if (this.badgeEl && badgeSource) {
            this.badgeEl.textContent = badgeSource.textContent.trim();
            this.badgeEl.hidden = false;
          } else if (this.badgeEl) {
            this.badgeEl.hidden = true;
          }

          this.priceEl.classList.add('sticky-atc-bar-custom__price--on-sale');
          return;
        }

        const regularSource =
          priceRoot.querySelector('.price__regular .price-item--regular') ||
          priceRoot.querySelector('.price-item--sale');

        if (regularSource) {
          this.saleEl.textContent = regularSource.textContent.trim();
        }

        if (this.compareEl) this.compareEl.hidden = true;
        if (this.compareValueEl) this.compareValueEl.textContent = '';
        if (this.badgeEl) {
          this.badgeEl.hidden = true;
          this.badgeEl.textContent = '';
        }

        this.priceEl.classList.remove('sticky-atc-bar-custom__price--on-sale');
      }

      syncSubmitState() {
        if (!this.submitButton || !this.submitLabel) return;

        const originalButton = this.getOriginalSubmitButton();
        if (!originalButton) return;

        const originalLabel = originalButton.querySelector('span');
        const labelText = originalLabel?.textContent?.trim();

        if (labelText) {
          this.submitLabel.textContent = labelText;
        }

        const isDisabled =
          originalButton.hasAttribute('disabled') ||
          originalButton.getAttribute('aria-disabled') === 'true';

        this.submitButton.disabled = isDisabled;
        this.submitButton.toggleAttribute('aria-disabled', isDisabled);
      }
    }
  );
}
